import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  ListItemAvatar,
  Avatar,
  Chip,
  ListItemButton
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { Tag } from '../../../types';
import { atendimentoService, AtendimentoTicket } from '../../../services/atendimentoService';
import api from '../../../services/api';

import ContatoAtendimentoDetalhesDialog from './ContatoAtendimentoDetalhesDialog';
import VisualizarTicketDialog from './VisualizarTicketDialog';

interface LinkedTagItemsDialogProps {
  open: boolean;
  onClose: () => void;
  tag: Tag | null;
  type: 'contatos' | 'tickets';
}

const LinkedTagItemsDialog: React.FC<LinkedTagItemsDialogProps> = ({ open, onClose, tag, type }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  // Estados para abrir os diálogos de detalhe
  const [selectedContato, setSelectedContato] = useState<any | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<AtendimentoTicket | null>(null);

  useEffect(() => {
    if (open && tag) {
      loadItems();
    } else {
      setItems([]);
    }
  }, [open, tag, type]);

  const loadItems = async () => {
    setLoading(true);
    try {
      if (type === 'tickets') {
        const tickets: any = await atendimentoService.getTickets({ tag_id: tag?.id });
        const list = Array.isArray(tickets) ? tickets : (tickets?.data || []);
        setItems(list);
      } else if (type === 'contatos') {
        const res: any = await atendimentoService.getContatos(undefined, tag?.id);
        const list = Array.isArray(res) ? res : (res?.data || []);
        setItems(list);
      }
    } catch (err) {
      console.error('Erro ao carregar itens da tag:', err);
    } finally {
      setLoading(false);
    }
  };

  const title = type === 'contatos' ? 'Contatos vinculados' : 'Tickets vinculados';
  const Icon = type === 'contatos' ? PersonIcon : ConfirmationNumberIcon;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 1.5, pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Icon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
            </Box>
            {tag && (
              <Chip
                label={tag.titulo}
                size="small"
                sx={{
                  bgcolor: tag.color,
                  color: '#fff',
                  fontWeight: 600,
                  height: 20,
                  fontSize: '0.65rem',
                }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Clique em um item para abrir os detalhes
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1, minHeight: 180, maxHeight: 360 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={150}>
              <CircularProgress size={28} />
            </Box>
          ) : items.length === 0 ? (
            <Typography textAlign="center" color="text.secondary" variant="caption" display="block" my={4}>
              Nenhum {type === 'contatos' ? 'contato' : 'ticket'} encontrado para esta tag.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {items.map((item) => {
                if (type === 'contatos') {
                  return (
                    <ListItem
                      key={item.id}
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() => setSelectedContato(item)}
                        sx={{ py: 0.6, px: 1, borderRadius: 1 }}
                      >
                        <ListItemAvatar sx={{ minWidth: 38 }}>
                          <Avatar
                            src={item.foto_perfil ? `/api/uploads/${item.foto_perfil}` : undefined}
                            sx={{ width: 30, height: 30, fontSize: '0.8rem' }}
                          >
                            <PersonIcon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.nome}
                          secondary={item.numero || 'Sem número'}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600, lineHeight: 1.2 }}
                          secondaryTypographyProps={{ variant: 'caption', fontSize: '0.7rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                } else {
                  const ticket = item as AtendimentoTicket;
                  return (
                    <ListItem
                      key={item.id}
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() => setSelectedTicket(ticket)}
                        sx={{ py: 0.6, px: 1, borderRadius: 1 }}
                      >
                        <ListItemAvatar sx={{ minWidth: 38 }}>
                          <Avatar sx={{ width: 30, height: 30 }}>
                            <ConfirmationNumberIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`Ticket #${ticket.id} - ${ticket.contato?.nome || 'Desconhecido'}`}
                          secondary={`Status: ${ticket.status}`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600, lineHeight: 1.2 }}
                          secondaryTypographyProps={{ variant: 'caption', fontSize: '0.7rem' }}
                        />
                        <Chip
                          size="small"
                          label={ticket.status}
                          color={ticket.status === 'fechado' ? 'success' : ticket.status === 'em_atendimento' ? 'warning' : 'primary'}
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                }
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 0.75 }}>
          <Button onClick={onClose} color="inherit" size="small">Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Detalhes do Contato */}
      <ContatoAtendimentoDetalhesDialog
        open={Boolean(selectedContato)}
        contato={selectedContato}
        onClose={() => setSelectedContato(null)}
      />

      {/* Diálogo de Detalhes do Ticket (Chat/Histórico) */}
      <VisualizarTicketDialog
        open={Boolean(selectedTicket)}
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
      />
    </>
  );
};

export default LinkedTagItemsDialog;
